import React, { useState, useEffect } from 'react';

interface DateObj {
  year: number;
  month: number;
}

interface Connection {
  candidateId: number;
  candidateName: string;
  linkedinUrl?: string | null;
  company: string;
  overlapMonths: number;

  // Candidate's role
  candidatePosition: string;
  candidateDescription?: string | null;
  candidateLocation?: string | null;
  candidateStartDate?: DateObj;
  candidateEndDate?: DateObj | null;

  // Connected person's role
  connectedPosition: string;
  connectedDescription?: string | null;
  connectedLocation?: string | null;
  connectedStartDate?: DateObj;
  connectedEndDate?: DateObj | null;

  // Overlap period
  overlapStart?: DateObj | null;
  overlapEnd?: DateObj | null;

  strength: 'strong' | 'medium' | 'weak';
}

interface ConnectionsModalProps {
  candidateId: number;
  candidateName: string;
  isOpen: boolean;
  onClose: () => void;
}

export const ConnectionsModal: React.FC<ConnectionsModalProps> = ({
  candidateId,
  candidateName,
  isOpen,
  onClose
}) => {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && candidateId) {
      fetchConnections();
    }
  }, [isOpen, candidateId]);

  const fetchConnections = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/connections/${candidateId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch connections');
      }

      const data = await response.json();
      setConnections(data.connections || []);
    } catch (err) {
      console.error('Error fetching connections:', err);
      setError('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getStrengthColor = (strength: string) => {
    switch (strength) {
      case 'strong': return 'text-green-600 bg-green-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'weak': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  // Color palette for companies
  const companyColors = [
    { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-800', header: 'bg-blue-50' },
    { bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-800', header: 'bg-green-50' },
    { bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-800', header: 'bg-purple-50' },
    { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-800', header: 'bg-orange-50' },
    { bg: 'bg-pink-100', border: 'border-pink-300', text: 'text-pink-800', header: 'bg-pink-50' },
    { bg: 'bg-teal-100', border: 'border-teal-300', text: 'text-teal-800', header: 'bg-teal-50' },
    { bg: 'bg-indigo-100', border: 'border-indigo-300', text: 'text-indigo-800', header: 'bg-indigo-50' },
    { bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-800', header: 'bg-red-50' },
  ];

  const getCompanyColor = (companyIndex: number) => {
    return companyColors[companyIndex % companyColors.length];
  };

  // Check if both people are currently working together (both end dates are null/Present)
  const isCurrentlyWorkingTogether = (conn: Connection) => {
    return conn.candidateEndDate === null && conn.connectedEndDate === null;
  };

  const getStrengthLabel = (strength: string) => {
    switch (strength) {
      case 'strong': return '2+ years';
      case 'medium': return '6-24 months';
      case 'weak': return '<6 months';
      default: return '';
    }
  };

  const formatDate = (date?: DateObj | null) => {
    if (!date) return 'Present';
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[date.month - 1]} ${date.year}`;
  };

  const formatDateRange = (start?: DateObj, end?: DateObj | null) => {
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  // Group connections by company
  const connectionsByCompany = connections.reduce((acc, conn) => {
    if (!acc[conn.company]) {
      acc[conn.company] = [];
    }
    acc[conn.company].push(conn);
    return acc;
  }, {} as Record<string, Connection[]>);

  return (
    <div className="modal-backdrop flex items-center justify-center" onClick={onClose}>
      <div className="modal-dialog max-w-5xl w-full mx-4 max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Network Connections</h2>
            <p className="text-sm text-gray-600 mt-1">{candidateName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-all duration-200 p-1.5 rounded-lg hover:bg-gray-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
          {loading && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading connections...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-8">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {!loading && !error && connections.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-600">No connections found</p>
              <p className="text-sm text-gray-500 mt-1">This candidate hasn't worked at the same companies as others in this project.</p>
            </div>
          )}

          {!loading && !error && connections.length > 0 && (
            <div className="space-y-6">
              <div className="text-sm text-gray-600">
                Found <span className="font-semibold">{connections.length}</span> connection{connections.length !== 1 ? 's' : ''} across {Object.keys(connectionsByCompany).length} {Object.keys(connectionsByCompany).length === 1 ? 'company' : 'companies'}
              </div>

              {Object.entries(connectionsByCompany).map(([company, conns], companyIndex) => {
                const colorScheme = getCompanyColor(companyIndex);
                return (
                <div key={company} className={`border-2 ${colorScheme.border} rounded-xl overflow-hidden transition-all duration-200 hover:shadow-card-hover`}>
                  <h3 className={`font-semibold ${colorScheme.text} ${colorScheme.header} px-4 py-3 flex items-center`}>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    {company}
                    <span className="ml-2 text-sm font-normal opacity-75">({conns.length} {conns.length === 1 ? 'connection' : 'connections'})</span>
                  </h3>
                  <div className="p-4">

                  <div className="space-y-4">
                    {conns.map((conn, idx) => (
                      <div key={idx} className="border-t border-gray-100 first:border-t-0 pt-3 first:pt-0">
                        {/* Connection header with name and strength */}
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="font-medium text-gray-900">{conn.candidateName}</div>
                            {conn.linkedinUrl && (
                              <a
                                href={conn.linkedinUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800"
                                title="View LinkedIn profile"
                              >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                </svg>
                              </a>
                            )}
                          </div>
                          <div className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${getStrengthColor(conn.strength)}`}>
                            {getStrengthLabel(conn.strength)}
                          </div>
                        </div>

                        {/* Overlap period - different styling for currently working vs worked together */}
                        {isCurrentlyWorkingTogether(conn) ? (
                          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-3">
                            <div className="text-xs font-semibold text-emerald-900 mb-1 flex items-center">
                              <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse"></span>
                              Currently working together
                            </div>
                            <div className="text-sm text-emerald-800">
                              Since {formatDate(conn.overlapStart || undefined)}
                              <span className="ml-2 text-xs">({conn.overlapMonths} {conn.overlapMonths === 1 ? 'month' : 'months'})</span>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-3">
                            <div className="text-xs font-semibold text-slate-700 mb-1">Worked together:</div>
                            <div className="text-sm text-slate-600">
                              {formatDateRange(conn.overlapStart || undefined, conn.overlapEnd)}
                              <span className="ml-2 text-xs">({conn.overlapMonths} {conn.overlapMonths === 1 ? 'month' : 'months'})</span>
                            </div>
                          </div>
                        )}

                        {/* Two-column layout for roles */}
                        <div className="grid grid-cols-2 gap-3">
                          {/* Original candidate's role */}
                          <div className="bg-gray-50/80 rounded-lg p-3">
                            <div className="text-xs font-semibold text-gray-500 mb-1">{candidateName}</div>
                            <div className="text-sm font-medium text-gray-900 mb-1">{conn.candidatePosition}</div>
                            {conn.candidateLocation && (
                              <div className="text-xs text-gray-600 mb-1 flex items-center">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                {conn.candidateLocation}
                              </div>
                            )}
                            <div className="text-xs text-gray-500">{formatDateRange(conn.candidateStartDate, conn.candidateEndDate)}</div>
                            {conn.candidateDescription && (
                              <div className="text-xs text-gray-600 mt-2 line-clamp-3">{conn.candidateDescription}</div>
                            )}
                          </div>

                          {/* Connected person's role */}
                          <div className="bg-gray-50/80 rounded-lg p-3">
                            <div className="text-xs font-semibold text-gray-500 mb-1">{conn.candidateName}</div>
                            <div className="text-sm font-medium text-gray-900 mb-1">{conn.connectedPosition}</div>
                            {conn.connectedLocation && (
                              <div className="text-xs text-gray-600 mb-1 flex items-center">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                {conn.connectedLocation}
                              </div>
                            )}
                            <div className="text-xs text-gray-500">{formatDateRange(conn.connectedStartDate, conn.connectedEndDate)}</div>
                            {conn.connectedDescription && (
                              <div className="text-xs text-gray-600 mt-2 line-clamp-3">{conn.connectedDescription}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="btn-secondary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
